<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :autoLoad="true"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import DataTable from '../../grid/DataTable.vue';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface SOServiceRow {
  so_service_id: Id;
  so_service_name?: string;
  so_service_category_name?: string;
  so_service_default_price?: number;
  [key: string]: unknown;
}

const emit = defineEmits<{
  'edit': [serviceId: Id];
  'delete': [serviceId: Id];
}>();

// Get i18n composable
const { t } = useI18n();

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed<string>(() => '/index.php/so/services/lista');

const tableColumns = computed(() => {
  return [
    {
      data: 'so_service_id',
      title: t('ID'),
      width: 80,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'so_service_name',
      title: t('Nome'),
      width: 250,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'so_service_category_name',
      title: t('Categoria'),
      width: 200,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'so_service_default_price',
      title: t('Preço Padrão'),
      width: 150,
      sortable: true,
      renderType: 'currency'
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `so_services_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    rowTestId: (data: Record<string, unknown>) => {
      const id = (data as SOServiceRow)?.so_service_id;
      return id == null ? '' : `so-services-row-${id}`;
    },
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV')
  };
});

// Methods
const loadData = (): void => {
  const stack = new Error().stack;
  log('SOServicesTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: SOServiceRow[]): void => {
  log('SOServicesTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('registros de serviços'));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('SOServicesTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar serviços') + ': ' + message);
};

const handleEditClick = (serviceId: Id): void => {
  log('SOServicesTable', 'handleEditClick', 'Edit clicked for ID', serviceId);
  emit('edit', serviceId);
};

const handleSaved = (): void => {
  log('SOServicesTable', 'event:so-services.saved', 'Received');
  loadData();
};

const handleDeleted = (): void => {
  log('SOServicesTable', 'event:so-services.deleted', 'Received');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('SOServicesTable', 'mounted', 'Setting up event listeners');

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  log('SOServicesTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('so-services.saved', handleSaved);
  eventBus.on('so-services.deleted', handleDeleted);
});

onBeforeUnmount(() => {
  log('SOServicesTable', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up event listeners
  eventBus.off('so-services.saved', handleSaved);
  eventBus.off('so-services.deleted', handleDeleted);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
